import { ChessWinner, SquareName } from '@libs/chess/types';
import { formatName } from '@libs/formatNames';

import { GameCardGame, GameCardTicking } from '../ui/components/GameCard';

type PlayerLike = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  rating?: number | null;
};

type ClockLike = {
  whiteLeftMs: number;
  blackLeftMs: number;
};

type LastMoveLike = {
  from: string;
  to: string;
};

type GameLike = {
  white: PlayerLike;
  black: PlayerLike;
  whiteSecondPlayer?: PlayerLike;
  blackSecondPlayer?: PlayerLike;
  currentFen: string;
  clock: ClockLike;
  result: string;
  nextMoveBy: string;
  timestampMs: number;
  lastMove?: LastMoveLike | null;
};

export function mapGameToGameCard(
  id: string,
  game: GameLike,
): GameCardGame & { whiteId: string; blackId: string; white2Id?: string; black2Id?: string } {
  return {
    id,
    fen: game.currentFen,
    whiteName: formatName(game.white),
    blackName: formatName(game.black),
    white2Name: game.whiteSecondPlayer ? formatName(game.whiteSecondPlayer) : undefined,
    black2Name: game.blackSecondPlayer ? formatName(game.blackSecondPlayer) : undefined,
    whiteId: game.white.id,
    blackId: game.black.id,
    white2Id: game.whiteSecondPlayer?.id,
    black2Id: game.blackSecondPlayer?.id,
    whiteRating: game.white.rating ?? undefined,
    blackRating: game.black.rating ?? undefined,
    white2Rating: game.whiteSecondPlayer?.rating ?? undefined,
    black2Rating: game.blackSecondPlayer?.rating ?? undefined,
    whiteTimeMs: game.clock.whiteLeftMs,
    blackTimeMs: game.clock.blackLeftMs,
    lastMove: mapLastMove(game.lastMove),
    winner: mapWinner(game.result),
    ticking: mapTicking(game),
  };
}

export function mapWinner(result: string): ChessWinner | undefined {
  if (result === 'BlackWin') return 'black';
  if (result === 'WhiteWin') return 'white';
  if (result === 'Draw') return 'draw';
  return undefined;
}

export function mapLastMove(
  move?: LastMoveLike | null,
): { from: SquareName; to: SquareName } | undefined {
  if (!move) return undefined;
  return {
    from: move.from as SquareName,
    to: move.to as SquareName,
  };
}

export function mapTicking(game: {
  result: string;
  nextMoveBy: string;
  timestampMs: number;
}): GameCardTicking | undefined {
  if (game.result !== 'InProgress') return undefined;

  return game.nextMoveBy === 'White'
    ? { color: 'white', timeFromMs: game.timestampMs }
    : { color: 'black', timeFromMs: game.timestampMs };
}
